import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, Zap, ShieldCheck, Info } from "lucide-react";
import { api, fmtEth } from "../lib/api";
import { useConfig, useWallet } from "../hooks/useHoodbank";
import { ensureChain, DISTRIBUTOR_ABI } from "../lib/wallet";

export default function Account() {
  const config = useConfig();
  const { address, connect, connecting } = useWallet();
  const [me, setMe] = useState(null);
  const [claimInfo, setClaimInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!address) return;
    try {
      const [w, c] = await Promise.all([api.get(`/wallet/${address}`), api.get(`/claim/${address}`)]);
      setMe(w.data);
      setClaimInfo(c.data);
    } catch {}
  }, [address]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 10000);
    return () => clearInterval(t);
  }, [refresh]);

  const doConnect = async () => {
    try { await connect(); } catch (e) { toast.error(e.message); }
  };

  const claim = async () => {
    if (!config?.distributor_address) return toast.info("Distributor not deployed yet");
    if (!claimInfo?.claimable) return toast.info("Nothing to claim yet");
    setBusy(true);
    try {
      const client = await ensureChain(config);
      const proof = claimInfo.proof;
      const hash = await client.writeContract({
        account: address,
        address: config.distributor_address,
        abi: DISTRIBUTOR_ABI,
        functionName: "claim",
        args: [BigInt(claimInfo.cumulative_amount), proof],
      });
      toast.success(`Claim submitted: ${hash.slice(0, 14)}…`);
      setTimeout(refresh, 6000);
    } catch (e) {
      toast.error(e.shortMessage || e.message);
    } finally { setBusy(false); }
  };

  const notDeployed = me?.configured === false;

  return (
    <div className="max-w-5xl mx-auto px-6 py-16" data-testid="account-page">
      <p className="font-mono text-xs tracking-[0.2em] text-[#00C805] uppercase mb-3">My account</p>
      <h1 className="font-heading font-bold text-3xl tracking-tight mb-10">Your HoodBank balance</h1>

      {!address ? (
        <div className="card-surface rounded-2xl p-14 flex flex-col items-start gap-6">
          <ShieldCheck size={28} className="text-[#00C805]" />
          <p className="text-neutral-400 text-sm leading-relaxed max-w-md">
            Connect your wallet to see your accrued ETH and claim it. Connecting is read-only —
            you only sign when you press Claim. This site never asks for your seed phrase or keys.
          </p>
          <button data-testid="connect-wallet-btn" onClick={doConnect} disabled={connecting}
            className="rounded-full bg-[#00C805] text-black font-semibold px-8 py-3.5 text-sm hover:bg-[#13D059] transition-colors duration-200 flex items-center gap-2 disabled:opacity-50">
            <Wallet size={16} /> {connecting ? "Connecting…" : "Connect Wallet"}
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          <p className="font-mono text-sm text-neutral-400 break-all" data-testid="connected-address">{address}</p>
          {notDeployed && (
            <div className="card-surface rounded-xl p-5 text-sm text-amber-400/90 font-mono" data-testid="account-awaiting-banner">
              Rewards distributor not deployed yet — claims go live once it's deployed & funded.
            </div>
          )}
          {me?.excluded && (
            <div className="card-surface rounded-xl p-5 text-sm text-neutral-400 font-mono flex items-center gap-2">
              <Info size={15} /> This address is excluded from rewards (LP / pool / contract).
            </div>
          )}
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="card-surface rounded-2xl p-8">
              <p className="font-mono text-[10px] tracking-[0.2em] text-neutral-500 uppercase">$BANK balance</p>
              <p data-testid="account-balance" className="font-mono font-semibold text-2xl mt-2">{me?.configured ? fmtEth(me.balance_hood, 2) : "—"}</p>
            </div>
            <div className="card-surface rounded-2xl p-8 border-t-2 border-t-[#00C805]">
              <p className="font-mono text-[10px] tracking-[0.2em] text-neutral-500 uppercase">Unclaimed ETH</p>
              <p data-testid="account-accrued" className="font-mono font-semibold text-2xl mt-2 text-[#00C805]">{me?.configured ? fmtEth(me.unclaimed_eth, 8) : "—"}</p>
            </div>
            <div className="card-surface rounded-2xl p-8">
              <p className="font-mono text-[10px] tracking-[0.2em] text-neutral-500 uppercase">Lifetime claimed</p>
              <p data-testid="account-lifetime" className="font-mono font-semibold text-2xl mt-2">{me?.configured ? fmtEth(me.lifetime_eth, 8) : "—"}</p>
            </div>
          </div>

          <div className="card-surface rounded-2xl p-8 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div>
              <h3 className="font-heading font-bold">Claim rewards</h3>
              <p className="text-sm text-neutral-500 mt-1 max-w-md">
                Your ETH accrues every cycle via a Merkle snapshot. Claim collects everything owed
                in one transaction — or the keeper may auto-push it to you.
              </p>
            </div>
            <button data-testid="claim-eth-btn" onClick={claim} disabled={busy || !claimInfo?.claimable}
              className="rounded-full bg-[#00C805] text-black font-semibold px-6 py-3 text-sm hover:bg-[#13D059] transition-colors duration-200 flex items-center gap-2 disabled:opacity-40">
              <Zap size={15} /> {busy ? "Claiming…" : claimInfo?.claimable ? `Claim ${fmtEth(claimInfo.unclaimed_eth, 6)} ETH` : "Nothing to claim"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
